import { computed, observable, runInAction } from 'mobx';
import { CmsData } from '@teliaee/gravity-forms';
import { OptionCardGroupValue } from 'telia-front-react';
import { Translate } from '@teliaee/telia-translate';
import { Package, PackageCapacity, PackageOption, PackageOptionGroup } from '../mobilebusiness/MobileBusinessInterfaces';
import { replaceDotsAndFormatToTwoDecimalPlacesRoundingUp } from '../util/PriceUtil';

// eslint-disable-next-line no-shadow
export enum NumberCount {
  SINGLE = 'single',
  MULTIPLE = 'multiple'
}

class MobileBusinessStore {
  @observable accessor selectedPackage: Package;
  @observable accessor submitted: boolean = false;
  @observable accessor visiblePackageOptionGroups: PackageOptionGroup[];
  @observable accessor numberCount: NumberCount = NumberCount.SINGLE;

  // Public only for testing
  @observable accessor packageOptionMap: Map<string, PackageOption> = new Map();
  @observable accessor selectedPackageOptionMap: Map<string, PackageOption[]> = new Map();
  @observable accessor packageOptionGroups: PackageOptionGroup[] = [];

  public initializePackageOptionMap(packageOptionGroups: PackageOptionGroup[]) {
    const newPackageOptionMap: Map<string, PackageOption> = new Map();
    packageOptionGroups.forEach((group) => {
      group.options.forEach((option, index) => {
        runInAction(() => {
          newPackageOptionMap.set(`${group.name}-${index}`, option);
        });
      });
    });
    runInAction(() => {
      this.packageOptionMap = newPackageOptionMap;
      this.packageOptionGroups = packageOptionGroups;
    });
  }

  public selectNumberCount = (numberCount: NumberCount) => {
    runInAction(() => {
      this.numberCount = numberCount;
      this.visiblePackageOptionGroups = this.getVisiblePackageOptionGroups();
    });
  };

  public selectPackage(mobilePackage: Package) {
    runInAction(() => {
      this.selectedPackageOptionMap = new Map();
      this.selectedPackage = mobilePackage;
      this.visiblePackageOptionGroups = this.getVisiblePackageOptionGroups();
    });
  }

  public setPackageOptions(group: string, value: OptionCardGroupValue) {
    if (!value) {
      this.selectedPackageOptionMap.delete(group);
    } else if (value instanceof Array) {
      runInAction(() => {
        this.selectedPackageOptionMap.set(
          group,
          value.map((item) => {
            return this.packageOptionMap.get(item);
          })
        );
      });
    } else {
      runInAction(() => {
        this.selectedPackageOptionMap.set(group, [this.packageOptionMap.get(value)]);
      });
    }
  }

  public getCmsData = (translate: Translate): CmsData[] => {
    const packageData = translate('mobileBusiness.form.partitiveData', {
      capacity: this.selectedPackage.capacity || translate('mobileBusiness.unlimited'),
      unit: this.selectedPackage.capacity ? translate(`mobileBusiness.capacityUnit.${this.selectedPackage.capacityUnit}`) : ''
    });
    const calls = this.selectedPackage.calls
      ? translate('mobileBusiness.form.limitedCalls', {
          calls: this.selectedPackage.calls
        })
      : translate('mobileBusiness.form.unlimitedCalls');

    const cmsData = [
      {
        key: 'mobileBusiness_package',
        value: `${packageData}, ${calls}, ${
          this.selectedPackage.isUnlimitedPlus || this.selectedPackage.isUnlimitedPlus ? '' : translate('mobileBusiness.form.oneSimCard')
        } (${replaceDotsAndFormatToTwoDecimalPlacesRoundingUp(this.packagePrice)} ${translate('mobileBusiness.eurPerMonth')}) ${
          this.employeePrice ? `(${replaceDotsAndFormatToTwoDecimalPlacesRoundingUp(this.employeePrice)} ${translate('mobileBusiness.eurPerMonth')})` : ''
        }`
      },
      { key: 'mobileBusiness_total', value: `${replaceDotsAndFormatToTwoDecimalPlacesRoundingUp(this.totalPrice)} ${translate('mobileBusiness.eurPerMonth')}` }
    ];

    for (const [key, value] of this.selectedPackageOptionMap.entries()) {
      cmsData.push({
        key: `mobileBusiness_${key}`,
        value: (value || []).map((option) => this.getFormStringForPackageOption(option, translate)).join('\n')
      });
    }

    return cmsData;
  };

  public clearPackageSelection() {
    runInAction(() => {
      this.selectedPackage = null;
      this.numberCount = NumberCount.SINGLE;
      this.setSubmitted(false);
    });
  }

  public setSubmitted(value: boolean) {
    runInAction(() => {
      this.submitted = value;
    });
  }

  @computed
  public get gaChoicesString() {
    if (this.selectedPackage) {
      const selectedPackage = `${this.selectedPackage.capacity}${this.selectedPackage.capacityUnit || ''}`;
      const packageOptions = this.getPackageOptionsGaString();
      return `${packageOptions}${packageOptions ? ' - ' : ''}${selectedPackage}`;
    }
    return '';
  }

  @computed
  public get selectedPackageOptions() {
    return Array.from(this.selectedPackageOptionMap.values()).reduce((a, b) => a.concat(...b), []);
  }

  @computed
  public get totalPrice() {
    return this.packagePrice + this.selectedPackageOptions.map((value) => value.price).reduce((a, b) => a + b, 0);
  }

  @computed
  private get hasOfferPrice() {
    return this.selectedPackage && this.selectedPackage.offerPrice !== null && this.selectedPackage.offerPrice !== undefined;
  }

  @computed
  private get hasEmployeeOfferPrice() {
    return this.selectedPackage && this.selectedPackage.employeePrice !== null && this.selectedPackage.employeeOfferPrice !== undefined;
  }

  @computed
  private get packagePrice() {
    if (!this.selectedPackage) {
      return 0;
    }
    return +(this.hasOfferPrice ? this.selectedPackage.offerPrice : this.selectedPackage.price);
  }

  @computed
  private get employeePrice() {
    if (!this.selectedPackage) {
      return 0;
    }
    return +(this.hasEmployeeOfferPrice ? this.selectedPackage.employeeOfferPrice : this.selectedPackage.employeePrice);
  }

  private getFormStringForPackageOption(option: PackageOption, translate: Translate) {
    return `${translate(option.nameKey)} (${replaceDotsAndFormatToTwoDecimalPlacesRoundingUp(option.price)} ${translate('mobileBusiness.eurPerMonth')})`;
  }

  public getVisiblePackageOptionGroups(): PackageOptionGroup[] {
    return (this.packageOptionGroups || []).filter((group) => this.shouldShowGroup(group));
  }

  private shouldShowGroup(group: PackageOptionGroup) {
    return (
      this.numberCount === NumberCount.MULTIPLE &&
      !(group.hiddenForCapacities?.length && group.hiddenForCapacities.some((c) => this.packageCapacityMatchesSelectedPackage(c)))
    );
  }

  private packageCapacityMatchesSelectedPackage(capacity: PackageCapacity) {
    return capacity.capacity === this.selectedPackage.capacity && capacity.capacityUnit === this.selectedPackage.capacityUnit;
  }

  private getPackageOptionsGaString() {
    return this.packageOptionGroups
      .map((group: PackageOptionGroup) => {
        if (!group.gaLabel) {
          return null;
        }
        const selectedOptions = this.selectedPackageOptionMap.get(group.name);
        if (!selectedOptions || !selectedOptions.length) {
          return null;
        }
        const selectedOptionGaLabels = selectedOptions.filter((selectedOption) => !!selectedOption.gaValue).map((selectedOption) => selectedOption.gaValue);
        if (!selectedOptionGaLabels || !selectedOptionGaLabels.length) {
          return null;
        }
        return `${group.gaLabel}: ${selectedOptionGaLabels.join(', ')}`;
      })
      .filter((group) => !!group)
      .join('; ');
  }
}

export default MobileBusinessStore;
